import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import type { BillType, User } from '../types';
import { useUpload } from '../lib/useUpload';
import { Modal, Alert, UploadProgress, inputClass, labelClass, primaryBtn, secondaryBtn } from './ui';

export const BILL_TYPE_LABELS: Record<BillType, string> = {
  diesel_supply: 'Diesel Supply',
  ekedc: 'EKEDC Bill',
  fumigation: 'Fumigation Bill',
  lawma: 'LAWMA Bill',
};

export const BILL_TYPES: BillType[] = ['diesel_supply', 'ekedc', 'fumigation', 'lawma'];

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmitted: () => void;
  role: string;
}

export default function BillFormModal({ open, onClose, onSubmitted, role }: Props) {
  const [billType, setBillType] = useState<BillType>('diesel_supply');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [litersSupplied, setLitersSupplied] = useState('');
  const [amountPerLiter, setAmountPerLiter] = useState('');
  const [density, setDensity] = useState('');
  const [lastReading, setLastReading] = useState('');
  const [directedTo, setDirectedTo] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [staff, setStaff] = useState<User[]>([]);
  const upload = useUpload();
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setError('');
    setTitle('');
    setDescription('');
    setAmount('');
    setLitersSupplied('');
    setAmountPerLiter('');
    setDensity('');
    setLastReading('');
    setDirectedTo('');
    setFiles([]);
    if (role !== 'staff') {
      api
        .get<User[]>('/auth/users?role=staff')
        .then(setStaff)
        .catch(() => setStaff([]));
    }
  }, [open, role]);

  const isDiesel = billType === 'diesel_supply';
  const isEkedc = billType === 'ekedc';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    upload.reset();
    setError('');
    try {
      const fd = new FormData();
      fd.append('bill_type', billType);
      if (title) fd.append('title', title);
      if (description) fd.append('description', description);
      if (amount) fd.append('amount', amount);
      if (isDiesel) {
        fd.append('liters_supplied', litersSupplied);
        if (amountPerLiter) fd.append('amount_per_liter', amountPerLiter);
        if (density) fd.append('density', density);
      }
      if (isEkedc) fd.append('last_reading', lastReading);
      if (role !== 'staff' && directedTo) fd.append('directed_to', directedTo);
      files.forEach((f) => fd.append('files', f));
      await upload.run('/bills', fd);
      onSubmitted();
      onClose();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Submit a Bill">
      {error && <Alert>{error}</Alert>}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Bill Type *</label>
            <select className={inputClass} required value={billType} onChange={(e) => setBillType(e.target.value as BillType)}>
              {BILL_TYPES.map((t) => (
                <option key={t} value={t}>{BILL_TYPE_LABELS[t]}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>Title</label>
            <input className={inputClass} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. May diesel delivery" />
          </div>
        </div>

        {role !== 'staff' && (
          <div>
            <label className={labelClass}>Directed To (Staff)</label>
            <select className={inputClass} value={directedTo} onChange={(e) => setDirectedTo(e.target.value)}>
              <option value="">Select staff member...</option>
              {staff.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
        )}

        {isDiesel && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className={labelClass}>Liters Supplied *</label>
                <input type="number" step="0.01" min="0" required className={inputClass} value={litersSupplied} onChange={(e) => setLitersSupplied(e.target.value)} placeholder="e.g. 2000" />
              </div>
              <div>
                <label className={labelClass}>Amount Per Liter</label>
                <input type="number" step="0.01" min="0" className={inputClass} value={amountPerLiter} onChange={(e) => setAmountPerLiter(e.target.value)} placeholder="e.g. 1350" />
              </div>
              <div>
                <label className={labelClass}>Density</label>
                <input type="number" step="0.001" min="0" className={inputClass} value={density} onChange={(e) => setDensity(e.target.value)} placeholder="e.g. 0.835" />
              </div>
            </div>
            <p className="text-xs text-gray-400 -mt-2">Enter the quantity, price and density of the diesel supply.</p>
          </>
        )}

        {isEkedc && (
          <>
            <div>
              <label className={labelClass}>Last Reading by EKEDC Staff *</label>
              <input type="number" step="0.01" min="0" required className={inputClass} value={lastReading} onChange={(e) => setLastReading(e.target.value)} placeholder="e.g. 24500 kWh" />
              <p className="text-xs text-gray-400 mt-1">Last meter reading taken by the EKEDC staff.</p>
            </div>
          </>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Amount (if known)</label>
            <input type="number" step="0.01" min="0" className={inputClass} value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="e.g. 2700000" />
          </div>
        </div>

        <div>
          <label className={labelClass}>Description</label>
          <textarea className={inputClass} rows={3} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Additional details about this bill..." />
        </div>

        <div>
          <label className={labelClass}>Attachments (receipts, NEPA bill, images)</label>
          <input
            type="file"
            multiple
            accept="image/jpeg,image/png,image/gif,image/webp,.pdf"
            className={inputClass}
            onChange={(e) => setFiles(Array.from(e.target.files || []))}
          />
          {files.length > 0 && (
            <p className="text-xs text-gray-500 mt-1">{files.length} file{files.length === 1 ? '' : 's'} selected</p>
          )}
        </div>

        <UploadProgress
          percent={upload.percent}
          phase={upload.phase}
          error={upload.error}
          onCancel={upload.cancel}
        />

        <div className="flex justify-end space-x-3">
          <button type="button" onClick={onClose} className={secondaryBtn}>Cancel</button>
          <button type="submit" disabled={upload.busy} className={primaryBtn}>
            {upload.phase === 'uploading'
              ? `Uploading ${upload.percent}%`
              : upload.phase === 'processing' ? 'Submitting...' : 'Submit Bill'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
